import type { ImageLoaderProps, ImageProps } from 'next/image';
import Image from 'next/image';
import { sortBy } from '@poseidon/shared-utils';

import { env } from '~/env';

const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps) => {
  let fullPathSrc = src;

  if (src.startsWith('/')) {
    const backendUrl = env.NEXT_PUBLIC_BACKEND_URL;

    // do not cache localhost urls
    if (backendUrl.startsWith('http://localhost')) {
      return src;
    }

    // cache local images
    fullPathSrc = backendUrl + src;
  }

  // cache rest urls
  const options =
    `/size:${width}:::` + (quality ? `/quality:${quality}` : '') + `/plain/${encodeURIComponent(fullPathSrc)}`;
  const result = env.NEXT_PUBLIC_IMAGE_RESIZING_SERVER_URL + '/insecure' + options;
  return result;
};

/**
 * Next.js optimized CDNImage with dynamic loader.
 * Uses next/image with a custom loader for CDN image resizing.
 */
export const CDNImage = ({ alt, ...props }: ImageProps) => {
  return <Image {...props} alt={alt} loader={imgProxyLoader} />;
};

type SlideSize = { w: number; url: string };

type DocImageDoc = {
  fileUrl: string;
  documentRepresentation: Array<{ sizes?: SlideSize[] | null }>;
};

const docImageLoader =
  (images: SlideSize[], srcDefault: string) =>
  ({ width }: ImageLoaderProps) => {
    if (images.length <= 0) return srcDefault;

    const found = images.find((img) => img.w > width);
    return found?.url ?? srcDefault;
  };

export const DocImage = ({ doc, className }: { className: string; doc: DocImageDoc }) => {
  const representation = doc.documentRepresentation[0];
  const images = representation == null ? [] : sortBy(representation?.sizes ?? [], (d) => d.w);
  const defaultImage = images.length > 0 ? (images[images.length - 1]?.url ?? doc.fileUrl) : doc.fileUrl;

  return (
    <Image src={defaultImage} alt="image" className={className} loader={docImageLoader(images, doc.fileUrl)} fill />
  );
};
